import { useMutation, useQuery, useQueryClient } from 'react-query';
import { MaestroAPI, maestroApiCall, useTranslation } from '@michelin/central-provider';
import { baseURL } from 'Types/APITypes';
import { File, QueryKeys } from 'Types';

interface B2BDealerLocatorFileResponse {
  exists: boolean,
  url: string,
  fileName: string,
}

interface PromosListingResponse {
  data: PromoItem[],
  total: number,
}

export interface PromoItem {
  name: string,
  file: File,
  status: string,
  log: string,
  message: string,
  create_user: string,
  update_user: string,
  created_date: string,
  update_date: string,
}

interface PromoSaveParams {
  data: PromoItem
}

const usePromosDescription = (promoStatus:string):string => {
  const { t } = useTranslation();
  switch (promoStatus) {
    case 'pending':
      return t('Pending');
    case 'processing':
      return t('Processing');
    case 'processed':
      return t('Processed');
    case 'layout_error':
      return t('Layout Error');
    case 'data_error':
      return t('Data Errors');
    case 'extension_error':
      return t('Extension Error');
    default:
      return `${promoStatus}`;
  }
};

const usePromosMessage = (promoStatus:string, message:any):string => {
  const { t } = useTranslation();
  switch (promoStatus) {
    case 'pending':
      return t('Pending');
    case 'processing':
      return t('Processing');
    case 'processed':
      return `${t('Processed')} ${message?.locations || ''}`;
    case 'layout_error':
      return t('Incorrect file layout. Please update the file to use the correct layout and upload again.');
    case 'data_error':
      return t('A data format error is preventing processing of the file. Please address the issue and upload again.');
    case 'extension_error':
      return t('Incorrect file extension. Please upload a CSV file.');
    default:
      return `${promoStatus} ${JSON.stringify(message)}`;
  }
};

const getRecentPromos = () => maestroApiCall({ method: 'GET', endpoint: 'admin/promos', baseURL }).then((data) => data);
const useGetPromos = () => useQuery<PromosListingResponse>(
  [QueryKeys.promosListing],
  () => getRecentPromos(),
  {
    refetchInterval: 10000,
  },
);

async function savePromo({ data }: PromoSaveParams) {
  const apiType: MaestroAPI = { method: 'PUT', endpoint: 'admin/promos', baseURL };
  const res = await maestroApiCall(apiType, data);
  return res;
}

const useSavePromo = () => {
  const queryClient = useQueryClient();
  return useMutation(savePromo, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.promosListing]);
    },
  });
};

const getB2BDealerLocatorFile = () => maestroApiCall({ method: 'GET', endpoint: 'admin/b2b-dealer-locator-file', baseURL }).then((data) => data);
const useGetB2BDealerLocatorFile = () => useQuery<B2BDealerLocatorFileResponse>(
  [QueryKeys.b2bFileDownload],
  () => getB2BDealerLocatorFile(),
  {
    refetchInterval: 800000,
  },
);

export {
  useGetPromos,
  useSavePromo,
  usePromosDescription,
  usePromosMessage,
  useGetB2BDealerLocatorFile,
};
